import { Models } from '../../../App';
import {
  Pages, QueryParamDataType, SchedulingQueryParams,
} from '../../constants';
import CustomersPageQueryParams from '../CustomersPageQueryParams';
import DriversPageQueryParams from '../DriversPageQueryParams';
import InsightsDetailsPageQueryParams from '../InsightsDetailsPageQueryParams';
import InsightsPageQueryParams from '../InsightsPageQueryParams';
import OrdersPageQueryParams from '../OrdersPageQueryParams';
import SchedulingPageQueryParams from '../SchedulingPageQueryParams';
import SettingsPageQueryParams from '../SettingsPageQueryParams';
import isValidDataType from './isValidDataType';

export type QueryParamsToReplace = { [key: string]: any };

export interface ValidatorArgs {
  routerStore: Models['router'],
  page: Pages,
  urlParams: Record<string, string>,
  queryParams: URLSearchParams,
}

interface QueryParamProperties {
  required?: boolean,
  type: QueryParamDataType;
  acceptedValues?: any;
  default?: any;
  customValidator?: (self: Models['router'], queryParams: URLSearchParams) => QueryParamsToReplace | undefined;
}

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

type PageQueryParams = PartialRecord<Pages, PartialRecord<any, QueryParamProperties>>;

export const pageQueryParams: PageQueryParams = {
  [Pages.CUSTOMERS]: CustomersPageQueryParams,
  [Pages.ORDERS]: OrdersPageQueryParams,
  [Pages.DRIVER_LISTING]: DriversPageQueryParams,
  [Pages.SETTINGS]: SettingsPageQueryParams,
  [Pages.INSIGHTS]: InsightsPageQueryParams,
  [Pages.INSIGHT_DETAILS]: InsightsDetailsPageQueryParams,
  [Pages.NOT_FOUND]: {
    [SchedulingQueryParams.SETTINGS]: {
      required: false,
      type: QueryParamDataType.STRING,
    },
  },
  [Pages.SCHEDULING]: SchedulingPageQueryParams,
};

const hasParamsToReplace = (diffs: QueryParamsToReplace | undefined): boolean => !!diffs && !!Object.values(diffs).length;

const validateQueryParams = ({ queryParams, page, routerStore }: ValidatorArgs) => {
  let queryParamsToReplace: QueryParamsToReplace = {};

  const possiblePageParamKeys = Object.keys(pageQueryParams[page] || {});
  const currentRequestParamKeys = [...queryParams.keys()];

  const allKeyForPage = [...new Set([...possiblePageParamKeys, ...currentRequestParamKeys])];

  allKeyForPage.forEach((key) => {
    const validParamFound = pageQueryParams?.[page]?.[key];

    // If param was found in requested URL or is required
    if (validParamFound && (queryParams.has(key) || validParamFound.required)) {
      const {
        type, acceptedValues, customValidator,
      } = validParamFound;

      // Validate the type of a valid key
      if (!isValidDataType<typeof acceptedValues>(type, queryParams.get(key), acceptedValues)) {
        queryParams.delete(key); // Remove key so additional validation can uses fallback logic
        const fallbackValue = validParamFound.required && validParamFound.default ? validParamFound.default : null;
        queryParamsToReplace = { ...queryParamsToReplace, [key]: fallbackValue };
      }

      const paramDiffs = customValidator?.(routerStore, queryParams);
      if (hasParamsToReplace(paramDiffs)) {
        queryParamsToReplace = { ...queryParamsToReplace, ...paramDiffs };
      }
    }

    // Requested param does not exist in available params for the page
    if (!validParamFound) {
      queryParamsToReplace = { ...queryParamsToReplace, [key]: null };
    }
  });

  const shouldReplaceParams = Object.keys(queryParamsToReplace).some(
    (key) => queryParams.get(key) !== String(queryParamsToReplace[key]),
  );

  if (shouldReplaceParams && hasParamsToReplace(queryParamsToReplace)) {
    routerStore.replaceQueryParams(queryParamsToReplace, true);
  }
};

export default validateQueryParams;
